import ExecutiveDiagramFrame from './ExecutiveDiagramFrame';

const CARD_W = 176;
const CARD_H = 168;
const CARD_TOP = 196;
const RAIL_Y = 148;
const HUB_XS = [140, 340, 540, 740];

const hubs = [
  {
    id: 'us',
    region: 'Americas',
    venue: 'New York',
    session: 'Premarket Through Aftermarket',
    instruments: ['Equities', 'Listed Options'],
    x: HUB_XS[0],
    color: 'sapphire',
  },
  {
    id: 'eu',
    region: 'Europe',
    venue: 'London · Continental',
    session: 'Listing Hours · Local Currency',
    instruments: ['International Equities'],
    x: HUB_XS[1],
    color: 'teal',
  },
  {
    id: 'asia',
    region: 'Asia-Pacific',
    venue: 'Tokyo · Hong Kong',
    session: 'Regional Sessions · Lunch-Aware',
    instruments: ['Regional Listings'],
    x: HUB_XS[2],
    color: 'amber',
  },
  {
    id: 'always',
    region: 'Continuous',
    venue: 'UTC Always-On',
    session: '24-Hour Lane',
    instruments: ['Digital Assets'],
    x: HUB_XS[3],
    color: 'violet',
  },
];

const palette = isDark => ({
  bg: isDark ? '#060d18' : '#f8fafc',
  grid: isDark ? '#1e293b' : '#e2e8f0',
  label: isDark ? '#f8fafc' : '#0f172a',
  muted: isDark ? '#94a3b8' : '#64748b',
  sapphire: isDark ? '#60a5fa' : '#2563eb',
  teal: isDark ? '#2dd4bf' : '#0d9488',
  amber: isDark ? '#fbbf24' : '#d97706',
  violet: isDark ? '#a78bfa' : '#7c3aed',
  emerald: isDark ? '#34d399' : '#059669',
  card: isDark ? '#0f172a' : '#ffffff',
});

const pillWidth = (text, maxW) =>
  Math.min(maxW, Math.max(88, text.length * 6.6 + 20));

const PATiSessionOrchestrationDiagram = ({ theme = 'dark' }) => {
  const isDark = theme === 'dark';
  const c = palette(isDark);
  const colorMap = {
    sapphire: c.sapphire,
    teal: c.teal,
    amber: c.amber,
    violet: c.violet,
  };

  return (
    <ExecutiveDiagramFrame
      theme={theme}
      caption="Supervised Session Handoff · Scoped State · Instrument-Aware Execution"
      className="pati-sess-frame"
    >
      <svg
        viewBox="0 0 880 500"
        className="w-full h-auto pati-sess-svg"
        role="img"
        aria-label="Global trading session orchestration across Americas, Europe, Asia-Pacific, and continuous markets with supervised handoff"
      >
        <defs>
          <linearGradient id="patiSessRail" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor={c.sapphire} stopOpacity="0.15" />
            <stop offset="35%" stopColor={c.teal} stopOpacity="0.35" />
            <stop offset="65%" stopColor={c.amber} stopOpacity="0.35" />
            <stop offset="100%" stopColor={c.violet} stopOpacity="0.2" />
          </linearGradient>
          <filter id="patiSessGlow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        <rect width="880" height="500" fill={c.bg} rx="12" />

        <text
          x="440"
          y="36"
          textAnchor="middle"
          fill={c.muted}
          fontSize="10"
          letterSpacing="2.5"
          fontWeight="600"
        >
          FOLLOW-THE-CLOCK · 24H MANDATE
        </text>

        <path
          d="M 90 86 Q 440 48 790 86"
          fill="none"
          stroke={c.grid}
          strokeWidth="1"
          opacity="0.5"
        />
        <path
          className="pati-sess-arc-fill"
          d="M 90 86 Q 440 48 790 86"
          fill="none"
          stroke="url(#patiSessRail)"
          strokeWidth="4"
          strokeLinecap="round"
        />

        {HUB_XS.map((x, i) => (
          <circle
            key={`arc-${i}`}
            cx={x}
            cy={72 + Math.abs(x - 440) * 0.03}
            r="4"
            fill={colorMap[hubs[i].color]}
            opacity="0.85"
          />
        ))}

        <text
          x="440"
          y="118"
          textAnchor="middle"
          fill={c.emerald}
          fontSize="9"
          letterSpacing="2"
          fontWeight="700"
        >
          SUPERVISED HANDOFF · NO CROSS-MARKET BLEED
        </text>

        <line
          x1="70"
          y1={RAIL_Y}
          x2="810"
          y2={RAIL_Y}
          stroke={c.grid}
          strokeWidth="1"
          opacity="0.4"
        />
        <line
          x1="70"
          y1={RAIL_Y}
          x2="810"
          y2={RAIL_Y}
          stroke="url(#patiSessRail)"
          strokeWidth="3"
          strokeLinecap="round"
          opacity="0.7"
        />

        <g
          className="pati-sess-handoff"
          style={{ transformOrigin: `${HUB_XS[0]}px ${RAIL_Y}px` }}
        >
          <circle
            cx={HUB_XS[0]}
            cy={RAIL_Y}
            r="7"
            fill={c.teal}
            filter="url(#patiSessGlow)"
          />
        </g>

        {[
          { x1: 228, x2: 252 },
          { x1: 428, x2: 452 },
          { x1: 628, x2: 652 },
        ].map(({ x1, x2 }, i) => (
          <g key={i} className={`pati-sess-arrow pati-sess-arrow-${i}`}>
            <line
              x1={x1}
              y1={RAIL_Y}
              x2={x2}
              y2={RAIL_Y}
              stroke={c.teal}
              strokeWidth="1"
              strokeDasharray="4 4"
              opacity="0.6"
            />
            <polygon
              points={`${x2},${RAIL_Y - 3} ${x2 + 6},${RAIL_Y} ${x2},${RAIL_Y + 3}`}
              fill={c.teal}
              opacity="0.7"
            />
            <text
              x={(x1 + x2) / 2 + 3}
              y={RAIL_Y - 12}
              textAnchor="middle"
              fill={c.muted}
              fontSize="8"
              letterSpacing="0.8"
            >
              Handoff
            </text>
          </g>
        ))}

        {hubs.map(hub => {
          const accent = colorMap[hub.color];
          const cardX = hub.x - CARD_W / 2;
          const pillMax = CARD_W - 24;
          return (
            <g key={hub.id} className={`pati-sess-hub pati-sess-hub-${hub.id}`}>
              <line
                x1={hub.x}
                y1={RAIL_Y}
                x2={hub.x}
                y2={CARD_TOP}
                stroke={accent}
                strokeWidth="1.5"
                opacity="0.5"
              />
              <circle
                cx={hub.x}
                cy={RAIL_Y}
                r="10"
                fill={c.card}
                stroke={accent}
                strokeWidth="2"
              />
              <circle
                cx={hub.x}
                cy={RAIL_Y}
                r="4"
                fill={accent}
                className={`pati-sess-node pati-sess-node-${hub.id}`}
              />

              <rect
                x={cardX}
                y={CARD_TOP}
                width={CARD_W}
                height={CARD_H}
                rx="12"
                fill={c.card}
                stroke={accent}
                strokeWidth="1.5"
              />
              <rect
                x={cardX}
                y={CARD_TOP}
                width={CARD_W}
                height="4"
                rx="2"
                fill={accent}
                opacity="0.85"
              />

              <text
                x={hub.x}
                y={CARD_TOP + 28}
                textAnchor="middle"
                fill={accent}
                fontSize="11"
                fontWeight="800"
                letterSpacing="1.2"
              >
                {hub.region.toUpperCase()}
              </text>
              <text
                x={hub.x}
                y={CARD_TOP + 50}
                textAnchor="middle"
                fill={c.label}
                fontSize="12"
                fontWeight="600"
              >
                {hub.venue}
              </text>
              <text
                x={hub.x}
                y={CARD_TOP + 70}
                textAnchor="middle"
                fill={c.muted}
                fontSize="9"
              >
                {hub.session}
              </text>

              {hub.instruments.map((inst, i) => {
                const w = pillWidth(inst, pillMax);
                const px = hub.x - w / 2;
                const py = CARD_TOP + 88 + i * 28;
                return (
                  <g key={inst}>
                    <rect
                      x={px}
                      y={py}
                      width={w}
                      height="22"
                      rx="11"
                      fill={isDark ? '#1e293b' : '#f1f5f9'}
                      stroke={accent}
                      strokeWidth="0.75"
                    />
                    <text
                      x={hub.x}
                      y={py + 15}
                      textAnchor="middle"
                      fill={accent}
                      fontSize="9"
                      fontWeight="600"
                    >
                      {inst}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        <path
          d="M 740 372 Q 440 404 140 372"
          fill="none"
          stroke={c.violet}
          strokeWidth="1"
          strokeDasharray="6 8"
          opacity="0.4"
          className="pati-sess-loop"
        />

        <text
          x="440"
          y="436"
          textAnchor="middle"
          fill={c.muted}
          fontSize="10"
          letterSpacing="1.2"
        >
          Each Session Ends Flat · State Scoped Per Venue
        </text>
      </svg>
    </ExecutiveDiagramFrame>
  );
};

export default PATiSessionOrchestrationDiagram;
